<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import * as d3 from 'd3'
import ChartTooltip from '@/components/ui/chart/ChartTooltip.vue'

interface ChartData {
  name: string
  value: number
  color: string
}

const props = withDefaults(
  defineProps<{
    data: ChartData[]
    width?: number
    height?: number
    showTooltip?: boolean
    valueFormatter?: (value: number) => string
    showCurrency?: boolean
    totalLabel?: string
    displayPercentage?: boolean
  }>(),
  {
    width: 200,
    height: 200,
    showTooltip: false,
    valueFormatter: (v: number) => v.toString(),
    showCurrency: false,
    totalLabel: 'Total',
    displayPercentage: true,
  },
)

const chartRef = ref<HTMLDivElement>()
const tooltipState = ref({
  visible: false,
  data: [] as ChartData[],
  position: { x: 0, y: 0 },
})

const total = computed(() => props.data.reduce((sum, item) => sum + item.value, 0))
const chartData = computed(() =>
  props.data.map((item) => ({
    ...item,
    percentage: (item.value / total.value) * 100,
  })),
)

const CHART_CONFIG = {
  margin: { side: 100, height: 25 },
  innerRadiusRatio: 0.6,
}

onMounted(() => {
  if (!chartRef.value) return

  const radius = Math.min(props.width, props.height) / 2
  const svg = d3
    .select(chartRef.value)
    .append('svg')
    .attr('width', props.width + CHART_CONFIG.margin.side * 2)
    .attr('height', props.height + CHART_CONFIG.margin.height + CHART_CONFIG.margin.height)
    .append('g')
    .attr(
      'transform',
      `translate(${props.width / 2 + CHART_CONFIG.margin.side},${props.height / 2 + CHART_CONFIG.margin.height})`,
    )

  if (total.value === 0) {
    svg
      .append('circle')
      .attr('r', radius)
      .style('fill', 'none')
      .style('stroke', '#666')
      .style('stroke-width', '1px')
    return
  }

  const arc = d3
    .arc()
    .innerRadius(radius * CHART_CONFIG.innerRadiusRatio)
    .outerRadius(radius)
  const pie = d3
    .pie<any>()
    .value((d) => d.value)
    .sort(null)
  const pieData = pie(chartData.value)

  const paths = svg
    .selectAll('path')
    .data(pieData)
    .enter()
    .append('path')
    .attr('d', arc as any)
    .attr('fill', (d: { data: { color: any } }) => d.data.color)

  if (props.showTooltip) {
    paths
      .on('mouseover', (e: MouseEvent, d: any) => updateTooltip(e, [d.data], true))
      .on('mousemove', (e: MouseEvent) => updateTooltip(e, tooltipState.value.data, true))
      .on('mouseout', () => (tooltipState.value.visible = false))
  }

  // Add percentage labels if displayPercentage is true
  if (props.displayPercentage) {
    // Calculate the outer arc for label positioning
    const outerArc = d3
      .arc()
      .innerRadius(radius * 1.1)
      .outerRadius(radius * 1.1)

    // Filter the data to only show percentages > 1%
    const filteredPieData = pieData.filter((d) => d.data.percentage > 1)

    // Add the percentage text
    svg
      .selectAll('text.percentage')
      .data(filteredPieData)
      .enter()
      .append('text')
      .attr('class', 'percentage')
      .attr('transform', (d: any) => {
        const pos = outerArc.centroid(d)
        // Adjust position based on the angle
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2
        pos[0] = radius * 1.2 * (midAngle < Math.PI ? 1 : -1)
        return `translate(${pos})`
      })
      .attr('dy', '.35em')
      .style('text-anchor', (d: any) => {
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2
        return midAngle < Math.PI ? 'start' : 'end'
      })
      .style('font-size', '12px')
      .style('fill', '#333')
      .text((d: any) => `${Math.round(d.data.percentage)} %`)

    // Add connecting lines
    svg
      .selectAll('polyline')
      .data(filteredPieData)
      .enter()
      .append('polyline')
      .attr('points', (d: any) => {
        const pos = outerArc.centroid(d)
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2
        pos[0] = radius * 1.15 * (midAngle < Math.PI ? 1 : -1)
        return [arc.centroid(d), outerArc.centroid(d), pos]
          .map((point) => point.join(','))
          .join(' ')
      })
      .style('fill', 'none')
      .style('stroke', '#999')
      .style('stroke-width', '1px')
  }
})

function updateTooltip(event: MouseEvent, data: ChartData[], visible: boolean) {
  const rect = chartRef.value?.getBoundingClientRect()
  if (!rect) return

  tooltipState.value = {
    visible,
    data,
    position: {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    },
  }
}
</script>

<template>
  <div class="relative">
    <div ref="chartRef" />

    <!-- Centre du donut -->
    <div
      class="absolute top-[52%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center"
    >
      <template v-if="data.length > 0">
        <div class="text-2xl font-bold">
          {{ valueFormatter(total) }}{{ showCurrency ? '€' : '' }}
        </div>
        <div class="text-sm" v-html="totalLabel" />
      </template>
    </div>

    <!-- Tooltip et légende existants -->
    <ChartTooltip
      v-if="showTooltip && tooltipState.visible"
      :data="tooltipState.data"
      :style="{
        height: 'auto',
        position: 'absolute',
        left: `${tooltipState.position.x}px`,
        top: `${tooltipState.position.y}px`,
        transform: 'translate(10px, -50%)',
        pointerEvents: 'none',
        zIndex: 50,
      }"
    />
  </div>
</template>
